import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { initSounds, playSoundIfEnabled } from '../utils/soundEffects';
import SoundToggle from './SoundToggle';

const { FiArrowLeft, FiRefreshCw, FiInfo, FiSettings, FiDollarSign, FiClock, FiHeart } = FiIcons;

const BauCuaGame = () => {
  const navigate = useNavigate();
  const [players, setPlayers] = useState([
    { name: 'Người chơi 1', balance: 1000, bets: { bau: 0, cua: 0, tom: 0, ca: 0, ga: 0, nai: 0 } },
    { name: 'Người chơi 2', balance: 1000, bets: { bau: 0, cua: 0, tom: 0, ca: 0, ga: 0, nai: 0 } }
  ]);
  const [currentPlayer, setCurrentPlayer] = useState(0);
  const [isRolling, setIsRolling] = useState(false);
  const [diceResults, setDiceResults] = useState(['bau', 'cua', 'tom']);
  const [showSettings, setShowSettings] = useState(false);
  const [showPrivacy, setShowPrivacy] = useState(false);
  const [challengeLevel, setChallengeLevel] = useState('mild');
  const [showHistory, setShowHistory] = useState(false);
  const [gameHistory, setGameHistory] = useState([]);
  const [betAmount, setBetAmount] = useState(50);
  const [showChallengeModal, setShowChallengeModal] = useState(false);
  const [currentChallenge, setCurrentChallenge] = useState('');
  const [roundCount, setRoundCount] = useState(1);

  const diceContainerRef = useRef(null);

  const symbols = {
    bau: { name: 'Bầu', emoji: '🍐' },
    cua: { name: 'Cua', emoji: '🦀' },
    tom: { name: 'Tôm', emoji: '🦐' },
    ca: { name: 'Cá', emoji: '🐟' },
    ga: { name: 'Gà', emoji: '🐓' },
    nai: { name: 'Nai', emoji: '🦌' }
  };

  const betOptions = [10, 50, 100, 200, 500];

  // Initialize sounds when component mounts
  useEffect(() => {
    initSounds();
    
    // Load saved data from local storage
    const savedPlayers = localStorage.getItem('bauCuaPlayers');
    const savedChallengeLevel = localStorage.getItem('bauCuaChallengeLevel');
    const savedHistory = localStorage.getItem('bauCuaGameHistory');
    
    if (savedPlayers) setPlayers(JSON.parse(savedPlayers));
    if (savedChallengeLevel) setChallengeLevel(savedChallengeLevel);
    if (savedHistory) setGameHistory(JSON.parse(savedHistory));
  }, []);

  // Save to local storage whenever data changes
  useEffect(() => {
    localStorage.setItem('bauCuaPlayers', JSON.stringify(players));
    localStorage.setItem('bauCuaChallengeLevel', challengeLevel);
    localStorage.setItem('bauCuaGameHistory', JSON.stringify(gameHistory));
  }, [players, challengeLevel, gameHistory]);

  const challenges = {
    mild: [
      "Hôn má đối phương trong 5 giây",
      "Massage vai cho đối phương trong 1 phút",
      "Ôm đối phương thật chặt trong 20 giây",
      "Hát một bài hát tình yêu ngắn cho đối phương",
      "Kể 3 điều bạn thích nhất về đối phương",
      "Hôn tay đối phương như một quý ông/quý cô",
      "Thì thầm điều ngọt ngào vào tai đối phương",
      "Nhìn vào mắt đối phương trong 30 giây không cười",
      "Vẽ một trái tim lên má đối phương bằng son môi",
      "Nói 'Anh/Em yêu em/anh' bằng 3 ngôn ngữ khác nhau"
    ],
    spicy: [
      "Cởi một món đồ của mình một cách quyến rũ",
      "Hôn cổ đối phương trong 30 giây",
      "Nhảy sexy cho đối phương xem trong 1 phút",
      "Massage lưng cho đối phương và hôn dọc theo cột sống",
      "Vuốt ve toàn thân đối phương trong 2 phút",
      "Hôn và cắn nhẹ tai đối phương",
      "Thì thầm điều khiêu khích vào tai đối phương",
      "Dùng đá lạnh vuốt ve từ cổ xuống ngực đối phương",
      "Hôn môi say đắm trong 45 giây",
      "Để đối phương vẽ lên cơ thể bạn bằng son môi"
    ],
    hot: [
      "Cởi đồ của đối phương bằng răng",
      "Dùng lưỡi liếm từ cổ xuống ngực đối phương",
      "Nhảy lap dance cho đối phương trong 2 phút",
      "Để đối phương trói tay bạn và làm điều họ muốn trong 3 phút",
      "Massage vùng nhạy cảm của đối phương trong 2 phút",
      "Cởi hết đồ và để đối phương ngắm trong 1 phút",
      "Tự làm mình thỏa mãn trước mặt đối phương trong 1 phút",
      "Dùng miệng khám phá vùng nhạy cảm của đối phương trong 2 phút",
      "Để đối phương 'sử dụng' bạn theo cách họ muốn trong 3 phút",
      "Thực hiện tư thế 69 trong 2 phút"
    ]
  };

  const placeBet = (symbol) => {
    if (isRolling) return;
    
    const player = players[currentPlayer];
    
    // Check if player has enough balance
    if (player.balance < betAmount) {
      playSoundIfEnabled('error');
      return;
    }
    
    const updatedPlayers = [...players];
    updatedPlayers[currentPlayer].bets[symbol] += betAmount;
    updatedPlayers[currentPlayer].balance -= betAmount;
    setPlayers(updatedPlayers);
    
    playSoundIfEnabled('click');
  };

  const rollDice = () => {
    if (isRolling) return;
    
    // Check if current player has placed any bets
    const currentPlayerBets = Object.values(players[currentPlayer].bets);
    if (currentPlayerBets.reduce((a, b) => a + b, 0) === 0) {
      playSoundIfEnabled('error');
      return;
    }
    
    setIsRolling(true);
    playSoundIfEnabled('diceRoll');
    
    // Animate dice rolling
    const rollInterval = setInterval(() => {
      const randomResults = [];
      for (let i = 0; i < 3; i++) {
        const symbolKeys = Object.keys(symbols);
        const randomIndex = Math.floor(Math.random() * symbolKeys.length);
        randomResults.push(symbolKeys[randomIndex]);
      }
      setDiceResults(randomResults);
    }, 100);
    
    // Stop rolling after 2 seconds and calculate results
    setTimeout(() => {
      clearInterval(rollInterval);
      
      const finalResults = [];
      for (let i = 0; i < 3; i++) {
        const symbolKeys = Object.keys(symbols);
        const randomIndex = Math.floor(Math.random() * symbolKeys.length);
        finalResults.push(symbolKeys[randomIndex]);
      }
      
      setDiceResults(finalResults);
      calculateResults(finalResults);
      
      setIsRolling(false);
    }, 2000);
  };

  const calculateResults = (results) => {
    const updatedPlayers = [...players];
    const currentPlayerIndex = currentPlayer;
    const player = updatedPlayers[currentPlayerIndex];
    
    // Count occurrences of each symbol in the results
    const resultCounts = results.reduce((counts, symbol) => {
      counts[symbol] = (counts[symbol] || 0) + 1;
      return counts;
    }, {});
    
    // Calculate winnings
    let totalWinnings = 0;
    let totalBets = 0;
    Object.keys(player.bets).forEach(symbol => {
      totalBets += player.bets[symbol];
      if (resultCounts[symbol]) {
        // Player wins their bet amount multiplied by the number of matching dice
        const winAmount = player.bets[symbol] * resultCounts[symbol];
        totalWinnings += winAmount;
      }
    });
    
    // Update player balance with winnings
    updatedPlayers[currentPlayerIndex].balance += totalWinnings;
    
    // Check if player lost (for challenge)
    const hasLost = totalWinnings === 0 && totalBets > 0;
    
    // Reset bets
    updatedPlayers[currentPlayerIndex].bets = { bau: 0, cua: 0, tom: 0, ca: 0, ga: 0, nai: 0 };
    
    // Update game history
    const historyEntry = {
      round: roundCount,
      player: player.name,
      results: [...results],
      bets: totalBets,
      winnings: totalWinnings
    };
    setGameHistory(prev => [historyEntry, ...prev.slice(0, 9)]);
    
    // Move to next player
    const nextPlayerIndex = (currentPlayerIndex + 1) % players.length;
    setCurrentPlayer(nextPlayerIndex);
    
    // Increment round count if we've gone through all players
    if (nextPlayerIndex === 0) {
      setRoundCount(prev => prev + 1);
    }
    
    // Show challenge if player lost
    if (hasLost) {
      const challengesList = challenges[challengeLevel];
      const randomChallenge = challengesList[Math.floor(Math.random() * challengesList.length)];
      setCurrentChallenge(randomChallenge);
      
      // Wait a bit for the results to be shown before showing the challenge
      setTimeout(() => {
        setShowChallengeModal(true);
        playSoundIfEnabled('notification');
      }, 1500);
    } else {
      playSoundIfEnabled('success');
    }
    
    setPlayers(updatedPlayers);
  };

  const resetGame = () => {
    setPlayers([
      { name: players[0].name, balance: 1000, bets: { bau: 0, cua: 0, tom: 0, ca: 0, ga: 0, nai: 0 } },
      { name: players[1].name, balance: 1000, bets: { bau: 0, cua: 0, tom: 0, ca: 0, ga: 0, nai: 0 } }
    ]);
    setCurrentPlayer(0);
    setDiceResults(['bau', 'cua', 'tom']);
    setRoundCount(1);
    setGameHistory([]);
    playSoundIfEnabled('notification');
  };

  const updatePlayerName = (index, name) => {
    const updatedPlayers = [...players];
    updatedPlayers[index].name = name;
    setPlayers(updatedPlayers);
  };

  const getLevelBadge = () => {
    switch (challengeLevel) {
      case 'mild':
        return { text: 'Nhẹ Nhàng', color: 'bg-green-500', emoji: '😊' };
      case 'spicy':
        return { text: 'Cay Nồng', color: 'bg-orange-500', emoji: '😏' };
      case 'hot':
        return { text: 'Nóng Bỏng', color: 'bg-red-500', emoji: '🔥' };
      default:
        return { text: 'Nhẹ Nhàng', color: 'bg-green-500', emoji: '😊' };
    }
  };

  if (showPrivacy) {
    return (
      <div className="min-h-screen p-4 flex flex-col">
        <motion.button
          initial={{ opacity: 0, x: -50 }}
          animate={{ opacity: 1, x: 0 }}
          onClick={() => setShowPrivacy(false)}
          className="self-start mb-4 bg-white/20 hover:bg-white/30 text-white p-3 rounded-full transition-all"
        >
          <SafeIcon icon={FiArrowLeft} className="text-xl" />
        </motion.button>
        <div className="flex-1 flex items-center justify-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-2xl w-full bg-white/10 backdrop-blur-lg p-8 rounded-3xl"
          >
            <h1 className="text-3xl font-bold text-white text-center mb-6">
              Điều Khoản & Quyền Riêng Tư
            </h1>
            <div className="space-y-6 text-white">
              <div>
                <h2 className="text-xl font-bold mb-2">1. Giới Hạn Độ Tuổi:</h2>
                <p className="text-pink-200">
                  Ứng dụng này chỉ dành cho người từ 18 tuổi trở lên. Bằng việc sử dụng ứng dụng này, bạn xác nhận mình đã đủ tuổi theo quy định.
                </p>
              </div>
              <div>
                <h2 className="text-xl font-bold mb-2">2. Quyền Sở Hữu:</h2>
                <p className="text-pink-200">
                  Game này được tạo và sở hữu độc quyền bởi Lux Homestay. Mọi hình thức sao chép, phân phối lại mà không có sự cho phép đều bị nghiêm cấm.
                </p>
              </div>
              <div>
                <h2 className="text-xl font-bold mb-2">3. Nội Dung:</h2>
                <p className="text-pink-200">
                  Trò chơi có thể chứa nội dung người lớn và chỉ nên chơi trong không gian riêng tư. Người dùng tự chịu trách nhiệm về việc sử dụng trò chơi này.
                </p>
              </div>
              <div>
                <h2 className="text-xl font-bold mb-2">4. Quyền Riêng Tư:</h2>
                <p className="text-pink-200">
                  Chúng tôi không thu thập, lưu trữ hay chia sẻ bất kỳ thông tin cá nhân nào của người dùng. Trò chơi được chơi hoàn toàn ngoại tuyến.
                </p>
              </div>
              <div>
                <h2 className="text-xl font-bold mb-2">5. Miễn Trừ Trách Nhiệm:</h2>
                <p className="text-pink-200">
                  Lux Homestay không chịu trách nhiệm về bất kỳ hậu quả nào phát sinh từ việc sử dụng trò chơi này. Người dùng tự chịu trách nhiệm về quyết định và hành động của mình.
                </p>
              </div>
              <div className="pt-4 border-t border-white/20">
                <p className="text-center text-pink-200">
                  © 2023 Lux Homestay. Bảo lưu mọi quyền.
                </p>
              </div>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setShowPrivacy(false)}
                className="w-full mt-4 bg-gradient-to-r from-pink-500 to-purple-500 hover:from-pink-600 hover:to-purple-600 text-white font-bold py-4 px-6 rounded-2xl shadow-lg transition-all duration-300"
              >
                Tôi Đã Hiểu & Đồng Ý
              </motion.button>
            </div>
          </motion.div>
        </div>
      </div>
    );
  }

  if (showSettings) {
    return (
      <div className="min-h-screen p-4 flex flex-col">
        <motion.button
          initial={{ opacity: 0, x: -50 }}
          animate={{ opacity: 1, x: 0 }}
          onClick={() => setShowSettings(false)}
          className="self-start mb-4 bg-white/20 hover:bg-white/30 text-white p-3 rounded-full transition-all"
        >
          <SafeIcon icon={FiArrowLeft} className="text-xl" />
        </motion.button>
        <div className="flex-1 flex items-center justify-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-md w-full bg-white/10 backdrop-blur-lg p-8 rounded-3xl"
          >
            <h1 className="text-3xl font-bold text-white text-center mb-6">
              Cài Đặt Bầu Cua
            </h1>
            <div className="space-y-6">
              <div>
                <label className="block text-pink-200 text-sm font-bold mb-2">
                  Tên người chơi 1:
                </label>
                <input
                  type="text"
                  value={players[0].name}
                  onChange={(e) => updatePlayerName(0, e.target.value)}
                  className="w-full p-3 rounded-xl bg-white/20 text-white placeholder-pink-200 border border-pink-300/30 focus:border-pink-400 focus:outline-none"
                />
              </div>
              
              <div>
                <label className="block text-pink-200 text-sm font-bold mb-2">
                  Tên người chơi 2:
                </label>
                <input
                  type="text"
                  value={players[1].name}
                  onChange={(e) => updatePlayerName(1, e.target.value)}
                  className="w-full p-3 rounded-xl bg-white/20 text-white placeholder-pink-200 border border-pink-300/30 focus:border-pink-400 focus:outline-none"
                />
              </div>

              <div>
                <label className="block text-pink-200 text-sm font-bold mb-2">
                  Mức độ thử thách:
                </label>
                <div className="grid grid-cols-3 gap-3">
                  <button
                    onClick={() => {
                      setChallengeLevel('mild');
                      playSoundIfEnabled('click');
                    }}
                    className={`p-3 rounded-xl text-sm font-medium transition-all ${
                      challengeLevel === 'mild'
                        ? 'bg-gradient-to-r from-green-500 to-blue-500 text-white'
                        : 'bg-white/10 text-white/80 hover:bg-white/20'
                    }`}
                  >
                    Nhẹ Nhàng 😊
                  </button>
                  <button
                    onClick={() => {
                      setChallengeLevel('spicy');
                      playSoundIfEnabled('click');
                    }}
                    className={`p-3 rounded-xl text-sm font-medium transition-all ${
                      challengeLevel === 'spicy'
                        ? 'bg-gradient-to-r from-yellow-500 to-orange-500 text-white'
                        : 'bg-white/10 text-white/80 hover:bg-white/20'
                    }`}
                  >
                    Cay Nồng 😏
                  </button>
                  <button
                    onClick={() => {
                      setChallengeLevel('hot');
                      playSoundIfEnabled('click');
                    }}
                    className={`p-3 rounded-xl text-sm font-medium transition-all ${
                      challengeLevel === 'hot'
                        ? 'bg-gradient-to-r from-red-500 to-pink-500 text-white'
                        : 'bg-white/10 text-white/80 hover:bg-white/20'
                    }`}
                  >
                    Nóng Bỏng 🔥
                  </button>
                </div>
              </div>

              <div className="pt-4 border-t border-white/20">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={resetGame}
                  className="w-full mb-4 bg-gradient-to-r from-red-500 to-orange-500 hover:from-red-600 hover:to-orange-600 text-white font-bold py-3 px-4 rounded-xl shadow-lg flex items-center justify-center gap-2"
                >
                  <SafeIcon icon={FiRefreshCw} className="text-lg" />
                  Bắt đầu trò chơi mới
                </motion.button>

                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => {
                    setShowSettings(false);
                    playSoundIfEnabled('success');
                  }}
                  className="w-full bg-gradient-to-r from-pink-500 to-purple-500 hover:from-pink-600 hover:to-purple-600 text-white font-bold py-3 px-4 rounded-xl shadow-lg flex items-center justify-center gap-2"
                >
                  Lưu cài đặt
                </motion.button>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    );
  }
  
  if (showHistory) {
    return (
      <div className="min-h-screen p-4 flex flex-col">
        <motion.button
          initial={{ opacity: 0, x: -50 }}
          animate={{ opacity: 1, x: 0 }}
          onClick={() => setShowHistory(false)}
          className="self-start mb-4 bg-white/20 hover:bg-white/30 text-white p-3 rounded-full transition-all"
        >
          <SafeIcon icon={FiArrowLeft} className="text-xl" />
        </motion.button>
        <div className="flex-1 flex items-center justify-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-md w-full bg-white/10 backdrop-blur-lg p-8 rounded-3xl"
          >
            <h1 className="text-3xl font-bold text-white text-center mb-6">
              Lịch Sử Chơi
            </h1>
            <div className="space-y-4">
              {gameHistory.length === 0 ? (
                <p className="text-center text-pink-200">Chưa có lịch sử chơi</p>
              ) : (
                gameHistory.map((entry, index) => (
                  <div key={index} className="bg-white/10 p-4 rounded-xl">
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-white font-bold">Vòng {entry.round}</span>
                      <span className="text-pink-200">{entry.player}</span>
                    </div>
                    <div className="flex justify-between items-center mb-2">
                      <div className="flex gap-2">
                        {entry.results.map((result, i) => (
                          <span key={i} className="text-2xl">{symbols[result].emoji}</span>
                        ))}
                      </div>
                      <div className={`text-sm ${entry.winnings > entry.bets ? 'text-green-400' : entry.winnings === 0 ? 'text-red-400' : 'text-yellow-400'}`}>
                        {entry.winnings > entry.bets 
                          ? `+${entry.winnings - entry.bets}`
                          : entry.winnings === 0 
                          ? `-${entry.bets}`
                          : `±0`}
                      </div>
                    </div>
                    <div className="text-xs text-pink-200 flex justify-between">
                      <span>Đặt cược: {entry.bets}</span>
                      <span>Thắng: {entry.winnings}</span>
                    </div>
                  </div>
                ))
              )}
              
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setShowHistory(false)}
                className="w-full mt-4 bg-gradient-to-r from-pink-500 to-purple-500 hover:from-pink-600 hover:to-purple-600 text-white font-bold py-3 px-4 rounded-xl shadow-lg"
              >
                Quay lại trò chơi
              </motion.button>
            </div>
          </motion.div>
        </div>
      </div>
    );
  }

  if (showChallengeModal) {
    return (
      <div className="min-h-screen p-4 flex items-center justify-center">
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          className="max-w-md w-full bg-white/10 backdrop-blur-lg p-8 rounded-3xl text-center"
        >
          <div className="text-5xl mb-4">😈</div>
          <h1 className="text-3xl font-bold text-white mb-6">
            Thử Thách!
          </h1>
          <div className={`${getLevelBadge().color} text-white text-xs font-bold px-3 py-1 rounded-full inline-block mb-4`}>
            {getLevelBadge().emoji} {getLevelBadge().text}
          </div>
          <div className="bg-white/10 p-6 rounded-xl mb-6">
            <h2 className="text-xl font-medium text-pink-200 mb-2">
              {players[(currentPlayer + 1) % 2].name} phải:
            </h2>
            <p className="text-2xl font-bold text-white">
              {currentChallenge}
            </p>
          </div>
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => {
              setShowChallengeModal(false);
              playSoundIfEnabled('click');
            }}
            className="w-full bg-gradient-to-r from-pink-500 to-purple-500 hover:from-pink-600 hover:to-purple-600 text-white font-bold py-4 px-6 rounded-xl shadow-lg"
          >
            Đã Hiểu!
          </motion.button>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-4 flex flex-col">
      <div className="flex justify-between items-center mb-4">
        <motion.button
          initial={{ opacity: 0, x: -50 }}
          animate={{ opacity: 1, x: 0 }}
          onClick={() => navigate('/')}
          className="bg-white/20 hover:bg-white/30 text-white p-3 rounded-full transition-all"
        >
          <SafeIcon icon={FiArrowLeft} className="text-xl" />
        </motion.button>
        <div className="flex gap-2">
          <motion.button
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            onClick={() => {
              setShowHistory(true);
              playSoundIfEnabled('click');
            }}
            className="bg-white/20 hover:bg-white/30 text-white p-3 rounded-full transition-all"
            title="Lịch sử"
          >
            <SafeIcon icon={FiClock} className="text-xl" />
          </motion.button>
          <motion.button
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            onClick={() => {
              setShowSettings(true);
              playSoundIfEnabled('click');
            }}
            className="bg-white/20 hover:bg-white/30 text-white p-3 rounded-full transition-all"
          >
            <SafeIcon icon={FiSettings} className="text-xl" />
          </motion.button>
          <SoundToggle className="bg-white/20" />
        </div>
      </div>

      <motion.h1
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-4xl font-bold text-white text-center mb-6"
      >
        🎲 Lắc Bầu Cua 🎲
      </motion.h1>

      <div className="flex flex-col md:flex-row gap-6">
        {/* Left panel - Player info and betting */}
        <div className="flex-1">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white/10 backdrop-blur-lg p-6 rounded-3xl mb-4"
          >
            <div className="flex justify-between items-center mb-4">
              <div>
                <h2 className="text-xl font-bold text-white">Lượt của:</h2>
                <p className="text-pink-200 text-lg">{players[currentPlayer].name}</p>
              </div>
              <div className="bg-gradient-to-r from-yellow-400 to-orange-500 p-3 rounded-xl">
                <div className="flex items-center gap-2">
                  <SafeIcon icon={FiDollarSign} className="text-white" />
                  <span className="text-xl font-bold text-white">{players[currentPlayer].balance}</span>
                </div>
              </div>
            </div>
            
            <div className="space-y-4">
              <h3 className="text-white font-bold">Đặt cược:</h3>
              <div className="grid grid-cols-3 gap-3">
                {Object.entries(symbols).map(([key, value]) => (
                  <motion.button
                    key={key}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => placeBet(key)}
                    disabled={isRolling || players[currentPlayer].balance < betAmount}
                    className={`
                      p-4 rounded-xl text-center transition-all relative
                      ${players[currentPlayer].bets[key] > 0 
                        ? 'bg-gradient-to-br from-yellow-400 to-orange-500' 
                        : 'bg-white/10 hover:bg-white/20'
                      }
                      ${isRolling || players[currentPlayer].balance < betAmount ? 'opacity-50 cursor-not-allowed' : ''}
                    `}
                  >
                    <div className="text-4xl mb-1">{value.emoji}</div>
                    <div className="text-sm text-white font-medium">{value.name}</div>
                    {players[currentPlayer].bets[key] > 0 && (
                      <div className="absolute -top-2 -right-2 bg-red-500 text-white text-xs font-bold rounded-full h-6 w-6 flex items-center justify-center">
                        {players[currentPlayer].bets[key]}
                      </div>
                    )}
                  </motion.button>
                ))}
              </div>

              <div className="mt-4">
                <h3 className="text-white font-bold mb-2">Mức cược:</h3>
                <div className="flex flex-wrap gap-2">
                  {betOptions.map(amount => (
                    <motion.button
                      key={amount}
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={() => {
                        setBetAmount(amount);
                        playSoundIfEnabled('click');
                      }}
                      className={`
                        px-3 py-1 rounded-lg text-sm font-medium transition-all
                        ${betAmount === amount 
                          ? 'bg-gradient-to-r from-pink-500 to-purple-500 text-white' 
                          : 'bg-white/10 text-white/80 hover:bg-white/20'
                        }
                      `}
                    >
                      {amount}
                    </motion.button>
                  ))}
                </div>
              </div>

              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={rollDice}
                disabled={isRolling}
                className="w-full bg-gradient-to-r from-pink-500 to-purple-500 hover:from-pink-600 hover:to-purple-600 disabled:from-gray-500 disabled:to-gray-600 disabled:cursor-not-allowed text-white font-bold py-4 px-6 rounded-xl shadow-lg mt-4 flex items-center justify-center gap-2"
              >
                {isRolling ? (
                  <div className="animate-spin rounded-full h-6 w-6 border-4 border-white border-t-transparent"></div>
                ) : (
                  <>
                    <span className="text-2xl">🎲</span>
                    Lắc Bầu Cua
                  </>
                )}
              </motion.button>
            </div>
          </motion.div>
          
          <div className="bg-white/10 backdrop-blur-lg p-4 rounded-3xl">
            <div className="grid grid-cols-2 gap-4">
              {players.map((player, index) => (
                <div 
                  key={index}
                  className={`p-3 rounded-xl ${currentPlayer === index ? 'bg-white/20' : 'bg-white/10'}`}
                >
                  <h3 className="text-white font-bold">{player.name}</h3>
                  <div className="flex items-center gap-1">
                    <SafeIcon icon={FiDollarSign} className="text-yellow-400 text-sm" />
                    <span className="text-yellow-400">{player.balance}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Right panel - Dice and results */}
        <div className="flex-1 flex flex-col">
          <motion.div
            ref={diceContainerRef}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white/10 backdrop-blur-lg p-6 rounded-3xl mb-4 flex-1 flex flex-col items-center justify-center"
          >
            <h2 className="text-xl font-bold text-white mb-6 text-center">Kết Quả</h2>
            
            <div className="flex justify-center gap-4 mb-6">
              {diceResults.map((result, index) => (
                <motion.div
                  key={index}
                  animate={{
                    rotateX: isRolling ? [0, 360, 720, 1080] : 0,
                    rotateY: isRolling ? [0, 360, 720, 1080] : 0,
                  }}
                  transition={{ duration: 2 }}
                  className="w-20 h-20 md:w-24 md:h-24 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-xl flex items-center justify-center text-4xl md:text-5xl shadow-lg"
                >
                  {symbols[result].emoji}
                </motion.div>
              ))}
            </div>
            
            <div className="text-center">
              <p className="text-pink-200 text-sm mb-2">Vòng: {roundCount}</p>
              <div className={`${getLevelBadge().color} text-white text-xs font-bold px-3 py-1 rounded-full inline-block`}>
                {getLevelBadge().emoji} {getLevelBadge().text}
              </div>
            </div>
          </motion.div>
          
          <div className="bg-white/10 backdrop-blur-lg p-4 rounded-3xl">
            <h3 className="text-lg font-bold text-white mb-4">Hướng Dẫn</h3>
            <ul className="list-disc list-inside text-pink-200 space-y-2 text-sm">
              <li>Đặt cược vào các biểu tượng: Bầu, Cua, Tôm, Cá, Gà, Nai</li>
              <li>Nhấn "Lắc Bầu Cua" để tung 3 viên xúc xắc</li>
              <li>Thắng khi biểu tượng bạn đặt cược xuất hiện trên xúc xắc</li>
              <li>Người thua sẽ phải thực hiện thử thách!</li>
            </ul>
            <div className="mt-4 pt-4 border-t border-white/20">
              <button
                onClick={() => setShowPrivacy(true)}
                className="text-pink-200 text-xs underline flex items-center"
              >
                <SafeIcon icon={FiInfo} className="mr-1 text-xs" />
                Điều khoản & Quyền riêng tư
              </button>
              <p className="text-pink-200 text-xs mt-2">
                © 2023 Lux Homestay. Bảo lưu mọi quyền.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BauCuaGame;